import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button } from '@/components/Button';
import { useEmbedControl } from '@/contexts/EmbedControl';
import { useLocation, useNavigate } from 'react-router-dom';
import { isString } from '../../utils/typeGuards/isString';
import { PATH_HOME, PATH_LOGIN } from '../../constants/pathConstatnts';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { setHasMembership, setIsLoggedIn } from '../../store/slices/membership';
import {
  hasMembershipSelector,
  isLoggedInSelector,
} from '@/store/selectors/membership';
import { useAppSelector } from '@/hooks/useAppSelector';

interface OneIDProps {
  clientId: string;
  responderPage: string;
  environment: string;
  listingId: string;
  isAllEventsLoaded?: boolean;
}

export const DisneyOneID: FunctionComponent<PropsWithChildren<OneIDProps>> = ({
  clientId,
  environment,
  responderPage,
  listingId,
  isAllEventsLoaded,
}) => {
  const control = useEmbedControl();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const hasMembership = useAppSelector(hasMembershipSelector);
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const [oneID, setOneID] = useState<any>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [lastClearedToken, setLastClearedToken] = useState<any>(null);

  const ONE_ID_CONFIG = {
    clientId,
    environment,
    responderPage,
  };

  const onMessage = useCallback(
    (e: MessageEvent) => {
      if (!isString(e.data) || !e.data.startsWith('embed')) {
        return;
      }
      const splitData = e.data.split('__');
      if (splitData[1] && splitData[1] === 'reservationCleared') {
        if (
          oneID &&
          isLoggedIn &&
          splitData[2] &&
          splitData[2] !== lastClearedToken
        ) {
          oneID.getGuest(false).then(createNewReservation);
          setLastClearedToken(splitData[2]);
        }
      }
    },
    [control, oneID, isLoggedIn],
  );

  const onOneIDInit = (init: any) => {
    if (init.loggedIn) {
      oneID.getGuest(false).then(onOneIDLogin);
    }
    setIsReady(true);
  };

  const onOneIDLogin = (guest: any) => {
    dispatch(setIsLoggedIn({ isLoggedIn: true }));
    if (isAllEventsLoaded) {
      createNewReservation(guest);
    }
    if (location.pathname == PATH_LOGIN && hasMembership !== false) {
      navigate(PATH_HOME);
    }
  };

  const onOneIDLogout = (_data: unknown) => {
    dispatch(setIsLoggedIn({ isLoggedIn: false }));
    if (control) {
      control.clearCurrentReservation();
    }
  };

  const onOneIDError = () => {};

  const doLogin = () => {
    oneID.launchLogin();
  };

  const doLogout = () => {
    oneID.logout();
  };

  const createNewReservation = (guest: any) => {
    // Get data and send request
    const membershipData = {
      listingId,
      membership: {
        submit_member: 'submit_member',
        swid: guest.profile.swid,
        disney_token: guest.token.access_token,
        id_token: guest.token.access_token,
      },
    };

    fetch('/api/memberships/', {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(membershipData),
    })
      .then((response) => {
        // Check if we need to confirm user's email - we have a redirect
        if (response.redirected) {
          window.location.href = response.url;
        }
        return response.json();
      })
      .then((data) => {
        if (data.success && control) {
          control.sendReservation(data);
          control.listingPriceLevelsUpdate();
          dispatch(
            setHasMembership({ hasMembership: true, membershipError: '' }),
          );
        }
        if (!data.success && data.error) {
          dispatch(
            setHasMembership({
              hasMembership: false,
              membershipError: data.error,
            }),
          );
          navigate(PATH_LOGIN);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src =
      environment == 'STAGE'
        ? 'https://stg.cdn.registerdisney.go.com/v4/OneID.js'
        : 'https://cdn.registerdisney.go.com/v4/OneID.js';
    script.async = true;
    script.onload = () => {
      try {
        const disneyOneID = window.OneID.get(ONE_ID_CONFIG);
        setOneID(disneyOneID);
        disneyOneID.init();
      } catch (e) {
        // If already initialized
        const disneyOneID = window.OneID.get();
        if (disneyOneID) {
          setOneID(disneyOneID);
          setIsReady(true);
        }
      }
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    // Add event listener with oneID and isLoggedIn dependencies
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [oneID, isLoggedIn]);

  useEffect(() => {
    if (oneID !== null) {
      oneID.on('init', onOneIDInit);
      oneID.on('login', onOneIDLogin);
      oneID.on('logout', onOneIDLogout);
      oneID.on('error', onOneIDError);
      oneID.getLoggedInStatus().then((status: boolean) => {
        dispatch(setIsLoggedIn({ isLoggedIn: status }));
        if (status) {
          oneID.getGuest(false).then(onOneIDLogin);
        }
        if (!status && location.pathname === PATH_HOME) {
          navigate(PATH_LOGIN);
        }
      });
    }
  }, [control, oneID, isAllEventsLoaded]);

  return (
    <>
      {location.pathname == PATH_LOGIN && hasMembership !== false && (
        <>
          {!isLoggedIn && (
            <Button onClick={doLogin} disabled={!isReady} size="small">
              Member login
            </Button>
          )}
          {isLoggedIn && (
            <Button onClick={doLogout} size="small">
              Logout
            </Button>
          )}
        </>
      )}
    </>
  );
};

export default DisneyOneID;
